// CTV-840: turn a rendered DOM node into a PNG the browser saves, one at a
// time or as a zip.
//
// html-to-image serialises the node into an SVG foreignObject, draws it onto
// a canvas at `pixelRatio`, and hands back a data URL. Two things it needs
// that a print sheet never did:
//
//   • Every image inside the node has to be fetchable with CORS, because the
//     canvas is tainted otherwise and toPng throws. Station logos come from
//     Supabase storage, which serves public objects with a permissive
//     Access-Control-Allow-Origin. `cacheBust` keeps the browser from reusing
//     a cached no-CORS copy of the same URL, which was fetched by the <img>.
//   • The node must be laid out at its real size. A CSS transform on an
//     ancestor (the Materials page scales the social square down to fit) is
//     fine, because the clone is measured from the node's own box.

import { toPng } from 'html-to-image';
import { zipSync } from 'fflate';

/** The node as a PNG data URL at `pixelRatio`, for showing in an <img>. */
export function renderNodeToPngUrl(node: HTMLElement, pixelRatio: number): Promise<string> {
  return toPng(node, { pixelRatio, cacheBust: true });
}

/** The node as PNG bytes at `pixelRatio`. */
export async function renderNodeToPng(node: HTMLElement, pixelRatio: number): Promise<Uint8Array> {
  return dataUrlToBytes(await renderNodeToPngUrl(node, pixelRatio));
}

/** A zip folder segment from a display name: no slashes, no leading dots, trailing slash. */
export function zipFolder(name: string): string {
  const clean = name
    .replace(/[\\/]+/g, ' ')
    .replace(/^\.+/, '')
    .trim();
  return `${clean || 'Untitled'}/`;
}

export function dataUrlToBytes(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(',');
  const binary = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Hand the browser a file to save. The anchor is a means to an end; it must not linger. */
export function saveBytes(bytes: Uint8Array, type: string, fileName: string): void {
  // slice() gives a Uint8Array over a plain ArrayBuffer, which is what Blob
  // accepts; the typed-array from a data URL or fflate may sit on a shared one.
  const blob = new Blob([bytes.slice()], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke after the click has been handed off; revoking synchronously can
  // cancel the save in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * The node as a PNG on the clipboard, so it can be pasted straight into a
 * post or an email (feedback 2026-09-08: "copy image" on the preview only
 * copied the TV chrome, since the ad is markup, not one image). Rejects
 * where the browser withholds image clipboard access.
 */
export async function copyNodeAsPng(node: HTMLElement, pixelRatio: number): Promise<void> {
  const bytes = await renderNodeToPng(node, pixelRatio);
  const blob = new Blob([bytes.slice()], { type: 'image/png' });
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
}

/**
 * CTV-856: the email signature on the clipboard as an HTML snippet, the way
 * signature generators do it. A raw picture pastes at its pixel size (a 2x
 * file filled the email) and a 1x picture is blurry on a retina screen; an
 * <img> with width and height set to the nominal size shows the 2x picture
 * at the right size, and the <a> around it means the client attaches the
 * link on paste, with no hyperlink step. A plain PNG rides along for a
 * target that only takes pictures, and the link as text for one that only
 * takes text. Gmail's signature editor takes none of these; it wants an
 * uploaded file (the page says so).
 */
export async function copySignatureAsHtml(
  node: HTMLElement,
  pixelRatio: number,
  opts: { width: number; height: number; href: string; alt: string },
): Promise<void> {
  const dataUrl = await renderNodeToPngUrl(node, pixelRatio);
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const html =
    `<a href="${esc(opts.href)}" target="_blank" rel="noopener">` +
    `<img src="${dataUrl}" width="${opts.width}" height="${opts.height}" alt="${esc(opts.alt)}"` +
    ` style="display:block;border:0;width:${opts.width}px;height:${opts.height}px" /></a>`;
  const png = new Blob([dataUrlToBytes(dataUrl).slice()], { type: 'image/png' });
  await navigator.clipboard.write([
    new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'image/png': png,
      'text/plain': new Blob([opts.href], { type: 'text/plain' }),
    }),
  ]);
}

export async function downloadNodeAsPng(
  node: HTMLElement,
  fileName: string,
  pixelRatio: number,
): Promise<void> {
  const bytes = await renderNodeToPng(node, pixelRatio);
  saveBytes(bytes, 'image/png', `${fileName}.png`);
}

/**
 * One zip holding the given PNGs, stored (not deflated: PNG is already
 * compressed). `folder` nests a file, e.g. "Dark/Evening anchors/".
 */
export function downloadPngsAsZip(
  files: Array<{ name: string; bytes: Uint8Array; folder?: string }>,
  zipName: string,
): void {
  const entries: Record<string, [Uint8Array, { level: 0 }]> = {};
  for (const f of files) entries[`${f.folder ?? ''}${f.name}.png`] = [f.bytes, { level: 0 }];
  const zipped = zipSync(entries);
  saveBytes(zipped, 'application/zip', `${zipName}.zip`);
}
